/**
 * Global React Query / mutation latency instrumentation.
 *
 * Records a CloudWatch RUM `custom.api_request_latency` event for EVERY query fetch and mutation,
 * measured from start to settle, without per-call-site wiring. Attached once to the app's
 * `QueryCache` and `MutationCache` (see `index.tsx`).
 *
 * The RUM logger lives in React context (`AurigaContext`), but the `QueryClient` is created outside
 * React. This module therefore uses the same decoupling pattern as `errorUtils.setGlobalErrorHandler`:
 * `AurigaContext` calls {@link setQueryLatencyLogger} once the logger exists, and the cache handlers
 * read that module-level reference. It reuses the existing {@link RumMetricsLogger.recordApiLatency}
 * (which also `console.debug`s the duration in development) — no new recording primitive.
 */

import { Mutation, QueryCacheNotifyEvent } from '@tanstack/react-query';
import RumMetricsLogger from '@utils/rum/RumMetricsLogger';

/**
 * Service attributed to a query/mutation when its key root isn't a known non-DeckManagement service.
 * The vast majority of app queries hit DeckManagement, so it is the default.
 */
const DEFAULT_SERVICE = 'DeckManagement';

/**
 * Maps a query/mutation key's leading segment (lowercased) to the RUM `service_name`, so latency is
 * attributed to the right backend rather than all landing under DeckManagement. Anything not listed
 * falls back to {@link DEFAULT_SERVICE}. Keep this aligned with the manual `recordApiLatency` service
 * names already in use (e.g. 'DimSum'). Extend when a new backend's queries are added.
 */
const SERVICE_BY_KEY_ROOT: Record<string, string> = {
  dimsum: 'DimSum',
  dimsumnode: 'DimSum',
};

/**
 * Resolves the RUM service name from a query/mutation key's leading segment.
 * @param key - the React Query key (query key or mutationKey)
 */
const serviceFromKey = (key: readonly unknown[] | undefined): string => {
  const root = key?.[0];
  if (typeof root !== 'string') {
    return DEFAULT_SERVICE;
  }
  return SERVICE_BY_KEY_ROOT[root.toLowerCase()] ?? DEFAULT_SERVICE;
};

// Module-level logger reference, set by AurigaContext once RUM is initialized (mirrors
// errorUtils.setGlobalErrorHandler). Null until then / when RUM is disabled — recording is a no-op.
let latencyLogger: RumMetricsLogger | null = null;

// Query fetch-start timestamps keyed by queryHash, so a settle event can compute duration.
// (React Query does not expose a public fetch-start time on query state, unlike mutations'
// `submittedAt`, so query timing is captured from the fetch/settle notify events.)
const queryFetchStartTimes = new Map<string, number>();

// Defensive cap: a fetch that never settles (cancelled/removed in-flight) is cleaned up by the
// `removed` and non-settle handling below, but this bounds the map even if an edge case slips
// through, so a long-lived session can't leak unboundedly. When exceeded, the oldest entry is
// evicted (insertion order).
const MAX_TRACKED_FETCHES = 1000;

/**
 * Registers the RUM logger used for latency recording. Called by AurigaContext.
 * @param logger - the app's RumMetricsLogger, or null to disable recording
 */
export const setQueryLatencyLogger = (logger: RumMetricsLogger | null): void => {
  latencyLogger = logger;
};

/** Records a fetch-start time, evicting the oldest entry if the map is at capacity. */
const trackFetchStart = (queryHash: string): void => {
  if (queryFetchStartTimes.size >= MAX_TRACKED_FETCHES) {
    const oldest = queryFetchStartTimes.keys().next().value;
    if (oldest !== undefined) {
      queryFetchStartTimes.delete(oldest);
    }
  }
  queryFetchStartTimes.set(queryHash, Date.now());
};

/**
 * Resolves the operation label for a query/mutation. Prefers the explicit `meta.rumOp` set by the
 * hook (the GraphQL operation name, via `extractGraphqlOperationName` — see `reactQueryConfig`'s
 * `rumOp` option), which is authoritative and can't leak/collapse. Falls back to the key's first
 * segment (always a static root like `page`/`scenario`/`deck`, never an id) for any query/mutation
 * not yet annotated — coarse but safe (bounded, never a cardinality explosion).
 * @param meta - the query/mutation `meta` object
 * @param key - the React Query key (query key or mutationKey)
 * @param fallback - label when neither meta.rumOp nor a usable key segment exists
 */
const operationLabel = (
  meta: Record<string, unknown> | undefined,
  key: readonly unknown[] | undefined,
  fallback: string,
): string => {
  const rumOp = meta?.rumOp;
  if (typeof rumOp === 'string' && rumOp.length > 0) {
    return rumOp;
  }
  const root = key?.[0];
  return typeof root === 'string' && root.length > 0 ? root : fallback;
};

/**
 * QueryCache notify-event handler: stamps fetch-start on a `fetch` action and records latency to RUM
 * when the query settles (success/error). Also clears tracked start times when a query is removed or
 * a fetch ends without settling (e.g. cancellation), so the start-time map can't leak over a
 * long-lived session. Attach via `new QueryCache().subscribe(handleQueryCacheEvent)`. A no-op when
 * no logger is registered.
 * @param event - the QueryCache notify event
 */
export const handleQueryCacheEvent = (event: QueryCacheNotifyEvent): void => {
  const { queryHash } = event.query;

  // Query GC'd/removed from the cache — drop any pending start time (covers removal while in-flight).
  if (event.type === 'removed') {
    queryFetchStartTimes.delete(queryHash);
    return;
  }

  if (event.type !== 'updated') {
    return;
  }

  if (event.action.type === 'fetch') {
    trackFetchStart(queryHash);
    return;
  }

  const settledSuccess = event.action.type === 'success';
  const settledError = event.action.type === 'error';
  if (!settledSuccess && !settledError) {
    // Any other terminal transition while a fetch is no longer in flight (e.g. a cancellation
    // rolling fetchStatus back to idle) would otherwise orphan the start time — drop it.
    if (!event.query.state.fetchStatus || event.query.state.fetchStatus === 'idle') {
      queryFetchStartTimes.delete(queryHash);
    }
    return;
  }

  const startTime = queryFetchStartTimes.get(queryHash);
  if (startTime === undefined) {
    return;
  }
  queryFetchStartTimes.delete(queryHash);

  latencyLogger?.recordApiLatency(
    serviceFromKey(event.query.queryKey),
    operationLabel(event.query.meta, event.query.queryKey, 'unknownQuery'),
    startTime,
    settledSuccess,
    'query',
  );
};

/**
 * MutationCache `onSettled` handler: records latency to RUM for a completed mutation, measured from
 * the mutation's `submittedAt` to now. Attach via `new MutationCache({ onSettled: handleMutationSettled })`.
 * A no-op when no logger is registered.
 * @param _data - mutation result (unused)
 * @param error - mutation error, if any (determines success flag)
 * @param _variables - mutation variables (unused)
 * @param _onMutateResult - onMutate context (unused)
 * @param mutation - the settled mutation (source of `submittedAt` + `mutationKey`)
 */
export const handleMutationSettled = (
  _data: unknown,
  error: unknown,
  _variables: unknown,
  _onMutateResult: unknown,
  mutation: Mutation<unknown, unknown, unknown>,
): void => {
  latencyLogger?.recordApiLatency(
    serviceFromKey(mutation.options.mutationKey),
    operationLabel(mutation.options.meta, mutation.options.mutationKey, 'unknownMutation'),
    mutation.state.submittedAt,
    error == null,
    'mutation',
  );
};
